import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Reveal = ({ children, className = '', delayMs = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: visible ? `${delayMs}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

export const HomePage = () => {
  const reasons = [
    {
      icon: ArrowsRightLeftIcon,
      title: 'Interoperabilidad real',
      description:
        'Conectamos imágenes médicas, sistemas clínicos y procesos digitales para que la información circule sin fricción.',
    },
    {
      icon: BuildingOffice2Icon,
      title: 'Encaje con tu infraestructura',
      description:
        'Trabajamos sobre lo que ya tenés: equipos, PACS y flujos del centro, sin forzar un cambio de plataforma completo.',
    },
    {
      icon: UserGroupIcon,
      title: 'Acompañamiento cercano',
      description:
        'Diseñamos e implementamos junto a tu equipo, con foco en el día a día operativo de la institución.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Expertise en flujo DICOM',
      description:
        'Entendemos el camino desde la captura hasta la disponibilidad del estudio, y lo convertimos en soluciones concretas.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-slate-900">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster="/images/screenshots/dicomfiles/dashboard.png"
        >
          <source src="/videos/banner.webm" type="video/webm" />
          <source src="/videos/banner.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-slate-900/55" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <p className="animate-fade-up text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-blue-200 mb-4">
              DICOMITY
            </p>
            <h1
              className="animate-fade-up text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ animationDelay: '100ms' }}
            >
              Hacemos realidad
              <span className="text-blue-300 block">la transformación digital</span>
            </h1>
            <p
              className="animate-fade-up text-xl text-slate-200 mb-8 max-w-3xl mx-auto"
              style={{ animationDelay: '220ms' }}
            >
              Diseñamos e implementamos soluciones de interoperabilidad que integran
              imágenes médicas, sistemas clínicos y procesos digitales en tu institución.
            </p>
            <div
              className="animate-fade-up flex flex-col sm:flex-row gap-4 justify-center"
              style={{ animationDelay: '320ms' }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Hablar con nosotros
                <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center justify-center border border-white/70 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-white/10 transition-colors duration-200"
              >
                Ver productos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quiénes somos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-3">
              Quiénes somos
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Acompañamos a instituciones de salud en su transformación digital
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              En DICOMITY diseñamos e implementamos soluciones de interoperabilidad
              pensadas para el entorno clínico: desde la captura y gestión de
              imágenes DICOM hasta la integración con los sistemas que ya usa tu centro.
            </p>
            <p className="text-lg text-gray-600">
              No vendemos tecnología aislada: construimos flujos que reducen trabajo
              manual, ordenan la información y hacen más simple el día a día operativo.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Por qué elegir DICOMITY */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-3">
              Por qué elegir DICOMITY
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Más que software: un socio de transformación
            </h2>
            <p className="text-xl text-gray-600">
              Elegirnos es apostar a interoperabilidad concreta, implementación
              cuidadosa y acompañamiento en el proceso de cambio.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <Reveal key={reason.title} delayMs={index * 100}>
                  <div className="transition-transform duration-300 hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-white text-blue-600 mb-4 border border-gray-100">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600">{reason.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Querés ver cómo encaja en tu institución?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contanos cómo trabajan hoy con imágenes y sistemas clínicos, y te
            mostramos el camino con DICOMITY.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center border border-white text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            Solicitar demo
            <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};
